import { Card } from "@/components/ui/card";
import * as React from "react";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  BookOpen,
  ListChecks,
  UserCheck,
  Calendar,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import EmptyBatchState from "./EmptyBatchState";

import { StudentStatsResponse } from "@/types";

interface Props {
   batchName : string;
   students : StudentStatsResponse[];
   topic : string;
}

const StudentRankingSection = ({ batchName, students, topic } : Props) => {
  const getRankBadgeStyle = (rank: number) => {
    if (rank === 1)
      return "bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 text-white shadow-lg shadow-yellow-500/50";
    if (rank === 2)
      return "bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500 text-white shadow-lg shadow-gray-400/50";
    if (rank === 3)
      return "bg-gradient-to-br from-orange-400 via-orange-500 to-orange-700 text-white shadow-lg shadow-orange-500/50";
    return "bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-md";
  };

  const getRankChangeIcon = (change: number) => {
    if (change > 0)
      return (
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          className="flex items-center gap-1 text-[#26BDB0] "
        >
          <TrendingUp size={16} />
          <span className="text-xs font-bold">+{change}</span>
        </motion.div>
      );
    if (change < 0)
      return (
        <motion.div
          initial={{ scale: 0, rotate: 180 }}
          animate={{ scale: 1, rotate: 0 }}
          className="flex items-center gap-1 text-red-600"
        >
          <TrendingDown size={16} />
          <span className="text-xs font-bold">{change}</span>
        </motion.div>
      );
    return (
      <div className="flex items-center gap-1 text-muted-foreground">
        <Minus size={16} />
        <span className="text-xs">0</span>
      </div>
    );
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 90) return "text-[#26BDB0] dark:text-[#26BDB0]";
    if (percentage >= 75) return "text-blue-600 dark:text-blue-600";
    if (percentage >= 60) return "text-yellow-600 dark:text-yellow-600";
    return "text-red-600 dark:text-red-600";
  };

  const hasStudents = Array.isArray(students) && students.length > 0;

  return (
    <div className="p-4 space-y-6 max-w-7xl mx-auto">
      {/* Leaderboard Section */}
      {hasStudents ? (
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex-column w-full items-center justify-center gap-3 mb-6"
          >
            <h2 className="text-2xl text-center font-extrabold  ">
              {batchName}
            </h2>
            <p className="h-3 text-semibold text-slate-400 text-center">
              {topic ? `Currently Learning : ${topic}` : " "}
            </p>
          </motion.div>

          <div className="grid gap-4">
            <AnimatePresence mode="popLayout">
              {students?.map((student: StudentStatsResponse, index: number) => {
                const isTopThree = student.rank <= 3;
                const hasRankChanged = student.rankChange !== 0;

                return (
                  <motion.div
                    key={student._id || index}
                    layout
                    layoutId={student._id}
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{
                      opacity: 1,
                      scale: 1,
                      y: 0,
                    }}
                    exit={{ opacity: 0, scale: 0.9, x: -100 }}
                    transition={{
                      layout: { type: "spring", stiffness: 300, damping: 30 },
                      opacity: { duration: 0.3 },
                      scale: { duration: 0.3 },
                    }}
                  >
                    <Card
                      className={`p-3 bg-white dark:bg-white/20 glass backdrop-blur-xl border border-white/20  
                      shadow-md transition-all duration-300 group 
                      ${
                        isTopThree
                          ? "border-white/40 dark:border-white/40 scale-[1.02]"
                          : "border-white/20 "
                      }
                      rounded-2xl`}
                    >
                      {/* Rank Change Flash Indicator */}
                      <AnimatePresence>
                        {hasRankChanged && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0, 0.5, 0] }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1.5, repeat: 2 }}
                            className={`absolute inset-0 rounded-lg pointer-events-none ${
                              student.rankChange > 0
                              ? "bg-[#26BDB0] dark:bg-[#127971]"
                                : "bg-red-500/10 dark:bg-red-500/10"
                            }`}
                          />
                        )}
                      </AnimatePresence>

                      {/* Header with Rank and Score */}
                      <div
                        className={`flex items-start justify-between ${
                          index == 0 && "mb-2"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Animated Rank Badge */}
                          <motion.div
                            whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
                            whileTap={{ scale: 0.95 }}
                            className={`relative flex items-center justify-center w-10 h-10 rounded-full ${getRankBadgeStyle(
                              student.rank
                            )} font-bold text-xl shadow-lg`}
                          >
                            {isTopThree && (
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{
                                  duration: 3,
                                  repeat: Infinity,
                                  ease: "linear",
                                }}
                                className="absolute inset-0 rounded-full"
                                style={{
                                  background: `conic-gradient(from 0deg, transparent, ${
                                    student.rank === 1
                                      ? "rgba(255, 251, 1, 0.78)"
                                      : student.rank === 2
                                      ? "rgba(156, 161, 175, 0.3)"
                                      : "rgba(194, 65, 12, 0.3)"
                                  }, transparent)`,
                                }}
                              />
                            )}
                            <span className="relative z-10">
                              #{student.rank}
                            </span>
                          </motion.div>

                          {/* Student Info */}
                          <div>
                            <motion.h3
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="text-xl font-bold text-gray-900 dark:text-gray-900"
                            >
                              {student.studentId?.fname}{" "}
                              {student.studentId?.lname}
                            </motion.h3>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                Previous: #{student.previousRank}
                              </span>
                              {getRankChangeIcon(student.rankChange)}
                            </div>
                          </div>
                        </div>

                        {/* Total Score with Animation */}
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 200,
                            delay: 0.2,
                          }}
                          className="text-right"
                        >
                          <motion.div
                            key={student.totalScore}
                            initial={{ scale: 1.5, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-600 dark:to-indigo-600 bg-clip-text text-transparent"
                          >
                            {student.totalScore.toFixed(1)}
                          </motion.div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                            Total Score
                          </div>
                        </motion.div>
                      </div>

                      {/* Stats Grid with Stagger Animation */}
                      {index == 0 && (
                        <motion.div
                          initial="hidden"
                          animate="visible"
                          variants={{
                            hidden: { opacity: 0 },
                            visible: {
                              opacity: 1,
                              transition: {
                                staggerChildren: 0.1,
                              },
                            },
                          }}
                          className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(180px,1fr))]"
                        >
                          {/* Attendance */}
                          <motion.div
                            variants={{
                              hidden: { opacity: 0, y: 20 },
                              visible: { opacity: 1, y: 0 },
                            }}
                            whileHover={{ scale: 1.05, y: -2 }}
                            className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-50 dark:to-blue-100 p-4 rounded-lg border border-blue-200 dark:border-blue-200 transition-all duration-300 group-hover:shadow-md"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <Calendar
                                size={18}
                                className="text-blue-600 dark:text-blue-600"
                              />
                              <span className="text-xs font-semibold text-gray-900 dark:text-gray-900">
                                Attendance
                              </span>
                            </div>
                            <motion.div
                              key={student.attendanceScore}
                              initial={{ scale: 1.2, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              className={`text-2xl font-bold ${getScoreColor(
                                student.attendanceScore
                              )}`}
                            >
                              {student.attendanceScore}%
                            </motion.div>
                            <div className="text-xs text-gray-600 dark:text-gray-600 mt-1">
                              {student.stats?.attendance.present}/
                              {student.stats?.attendance.total} present
                            </div>
                          </motion.div>

                          {/* Assignments */}
                          <motion.div
                            variants={{
                              hidden: { opacity: 0, y: 20 },
                              visible: { opacity: 1, y: 0 },
                            }}
                            whileHover={{ scale: 1.05, y: -5 }}
                            className="bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-50 dark:to-teal-100 p-4 rounded-lg border border-teal-200 dark:border-teal-200 transition-all duration-300 group-hover:shadow-md"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <BookOpen
                                size={18}
                                className="text-[#26BDB0] dark:text-[#26BDB0]"
                              />
                              <span className="text-xs font-semibold text-gray-900 dark:text-gray-900">
                                Assignments
                              </span>
                            </div>
                            <motion.div
                              key={student.assignmentScore}
                              initial={{ scale: 1.2, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              className={`text-2xl font-bold ${getScoreColor(
                                student.assignmentScore
                              )}`}
                            >
                              {student.assignmentScore}
                            </motion.div>
                            <div className="text-xs text-gray-600 dark:text-gray-600 mt-1">
                              {student.stats?.assignments.graded}/
                              {student.stats?.assignments.total} graded
                              {student.stats?.assignments.avgScore && (
                                <span>
                                  {" "}
                                  · Avg:{" "}
                                  {student.stats?.assignments.avgScore.toFixed(
                                    1
                                  )}
                                </span>
                              )}
                            </div>
                          </motion.div>

                          {/* Tasks */}
                          <motion.div
                            variants={{
                              hidden: { opacity: 0, y: 20 },
                              visible: { opacity: 1, y: 0 },
                            }}
                            whileHover={{ scale: 1.05, y: -5 }}
                            className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-50 dark:to-purple-100 p-4 rounded-lg border border-purple-200 dark:border-purple-200 transition-all duration-300 group-hover:shadow-md"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <ListChecks
                                size={18}
                                className="text-purple-600 dark:text-purple-600"
                              />
                              <span className="text-xs font-semibold text-gray-900 dark:text-gray-900">
                                Tasks
                              </span>
                            </div>
                            <motion.div
                              key={student.taskScore}
                              initial={{ scale: 1.2, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              className={`text-2xl font-bold ${getScoreColor(
                                student.taskScore
                              )}`}
                            >
                              {student.taskScore}
                            </motion.div>
                            <div className="text-xs text-gray-600 dark:text-gray-600 mt-1">
                              {student.stats?.tasks.graded}/
                              {student.stats?.tasks.total} graded
                              {student.stats?.tasks.avgScore && (
                                <span>
                                  {" "}
                                  · Avg:{" "}
                                  {student.stats?.tasks.avgScore.toFixed(1)}
                                </span>
                              )}
                            </div>
                          </motion.div>

                          {/* Interviews */}
                          <motion.div
                            variants={{
                              hidden: { opacity: 0, y: 20 },
                              visible: { opacity: 1, y: 0 },
                            }}
                            whileHover={{ scale: 1.05, y: -5 }}
                            className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-50 dark:to-orange-100 p-4 rounded-lg border border-orange-200 dark:border-orange-200 transition-all duration-300 group-hover:shadow-md"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <UserCheck
                                size={18}
                                className="text-orange-600 dark:text-orange-600"
                              />
                              <span className="text-xs font-semibold text-gray-900 dark:text-gray-900">
                                Interviews
                              </span>
                            </div>
                            <motion.div
                              key={student.interviewScore}
                              initial={{ scale: 1.2, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              className={`text-2xl font-bold ${getScoreColor(
                                student.interviewScore
                              )}`}
                            >
                              {student.interviewScore}
                            </motion.div>
                            <div className="text-xs text-gray-600 dark:text-gray-600 mt-1">
                              {student.stats?.interviews?.completed}/
                              {student.stats?.interviews.total} completed
                              {student.stats?.interviews.avgScore && (
                                <span>
                                  {" "}
                                  · Avg:{" "}
                                  {student.stats?.interviews.avgScore.toFixed(
                                    1
                                  )}
                                </span>
                              )}
                            </div>
                          </motion.div>
                        </motion.div>
                      )}

                      {/* Weights Distribution */}
                      {/* {
                        index === 0 && <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="border-t border-gray-200 dark:border-gray-700 pt-4"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <Award size={16} className="text-gray-600 dark:text-gray-400" />
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Score Weights
                          </span>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="text-center p-2 rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              Attendance
                            </div>
                            <div className="text-sm font-bold text-blue-600 dark:text-blue-400">
                              {student.weights?.attendance}%
                            </div>
                          </motion.div>
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="text-center p-2 rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              Assignment
                            </div>
                            <div className="text-sm font-bold text-[#26BDB0] dark:text-green-400">
                              {student.weights?.assignment}%
                            </div>
                          </motion.div>
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="text-center p-2 rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              Task
                            </div>
                            <div className="text-sm font-bold text-purple-600 dark:text-purple-400">
                              {student.weights?.task}%
                            </div>
                          </motion.div>
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            className="text-center p-2 rounded-md bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                              Interview
                            </div>
                            <div className="text-sm font-bold text-orange-600 dark:text-orange-400">
                              {student.weights?.interview}%
                            </div>
                          </motion.div>
                        </div>
                      </motion.div>

                      } */}
                      {/* Last Updated */}
                      {/* <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-right flex items-center justify-end gap-1">
                        <Calendar size={12} />
                        Last updated:{" "}
                        {new Date(student.lastUpdated).toLocaleString("en-IN")}
                      </div> */}
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      ) : (
          <EmptyBatchState batchName={batchName} topic={topic} />
      )}
    </div>
  );
};

export default StudentRankingSection;
